import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { FieldDef } from 'app/core/models';
import { StateBase } from '../state-base';
import { Field } from './actions';
import { TimeSeriesChartService } from '@shared/modules/time-series-charts/time-series-charts.service';
import { RelatedToType } from '@shared/enums';
import { tap } from 'rxjs';

export type DropdownOptionNames =
  | 'project_stages'
  | 'property_type'
  | 'project_type'
  | 'user'
  | 'investment_type'
  | 'team'
  | 'vehicle'
  | 'savedFilter'
  | 'pipeline'
  | 'market_statuses'
  | 'project_templates'
  | 'loan_purposes'
  | 'categories'
  | 'project_phases'
  | 'call_outcomes'
  | 'meeting_outcomes'
  | 'engagement_activity_types'
  | 'social_media_sources'
  | 'task_roles'
  | 'workflow_states'
  | 'task_list_templates';

export interface FieldDefsStateModel {
  timeSeriesFields: { [relatedToType: string]: FieldDef[] };
  dropdownOptions: { [option_name in DropdownOptionNames]?: any[] };
}

@State<FieldDefsStateModel>({
  name: new StateToken('fieldDefsState'),
  defaults: {
    timeSeriesFields: {},
    dropdownOptions: {},
  },
})
@Injectable()
export class FieldDefsState extends StateBase {
  constructor(private timeSeriesChartService: TimeSeriesChartService) {
    super();
  }

  /**
   * @deprecated Use ObjectsManagerState.getAllFields
   */
  @Selector()
  static getDropdownOptions(state: FieldDefsStateModel) {
    return state.dropdownOptions;
  }

  static selectDropdownOptionsBy(optionName: DropdownOptionNames, displayHidden = false) {
    return createSelector([FieldDefsState], (state: FieldDefsStateModel) =>
      state.dropdownOptions[optionName]?.filter((option) => !option?.hidden || displayHidden),
    );
  }

  static getTimeSeriesFields(relatedToType: RelatedToType | string) {
    return createSelector([FieldDefsState], (state: FieldDefsStateModel) => {
      return state.timeSeriesFields?.[relatedToType] || [];
    });
  }

  @Action(Field.SetDropdownOptions)
  setDropdownOptions(
    context: StateContext<FieldDefsStateModel>,
    {
      options,
    }: {
      options: { [option_name: string]: any[] };
    },
  ) {
    const currentOptions = context.getState().dropdownOptions;

    context.patchState({ dropdownOptions: { ...currentOptions, ...options } });
  }

  @Action(Field.FetchTimeSeries)
  fetchTimeSeriesFields(context: StateContext<FieldDefsStateModel>, { relatedToType }: Field.FetchTimeSeries) {
    this.timeSeriesChartService.relatedToType = relatedToType;

    return this.timeSeriesChartService.getTimeSeriesFields(false).pipe(
      tap((timeSeriesFieldDefs) => {
        const transformedTimeSeriesFields = timeSeriesFieldDefs.map((field) => ({
          ...field,
          name: `${field.name}(cf_name: "${field.name}")`,
          usedForTimeSeries: true,
          related_to_type: relatedToType as RelatedToType,
          _parentFieldName: 'Time Series',
          _parentFieldLabel: 'Time Series',
          meta: {
            ...field.meta,
            group: {
              key: 'Time Series',
              label: 'Time Series',
            },
          },
        }));

        const currentTimeSeriesFields = context.getState().timeSeriesFields || {};

        context.patchState({
          timeSeriesFields: {
            ...currentTimeSeriesFields,
            [relatedToType]: transformedTimeSeriesFields,
          },
        });
      }),
    );
  }
}
